import { useState, type FormEvent, type ReactNode } from 'react';
import { Save } from 'lucide-react';
import { PLATFORMS, PLATFORM_NAMES, type PlatformName } from '@cp/shared';
import { Button, Dialog, Field, Input, Select } from '@/components/ui';
import { useSetPlatform } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorField, errorText } from '@/utils/errors';
import type { SiteDetail } from '@/types/api';

const AUTO = 'auto';
type Choice = PlatformName | typeof AUTO;

/**
 * Correcting what the panel recognised: the platform, the admin address, or
 * both. "Определять автоматически" and an empty address hand them back to detection.
 */
export function PlatformDialog({ site, onClose }: { readonly site: SiteDetail; readonly onClose: () => void }): ReactNode {
  const byOperator = site.platformInfo?.source === 'operator';
  const [platform, setPlatform] = useState<Choice>(byOperator && site.platformInfo?.name ? (site.platformInfo.name as PlatformName) : AUTO);
  const [adminUrl, setAdminUrl] = useState(site.adminUrlByOperator ? (site.adminUrl ?? '') : '');

  const save = useSetPlatform(site.id, {
    onSuccess: () => {
      toast.success('Сохранено');
      onClose();
    },
  });

  const detected = !byOperator && site.platformInfo?.name ? PLATFORMS[site.platformInfo.name as PlatformName]?.label : null;
  const options = [
    { value: AUTO as Choice, label: detected ? `Определять автоматически (сейчас: ${detected})` : 'Определять автоматически' },
    ...PLATFORM_NAMES.map((name) => ({ value: name as Choice, label: PLATFORMS[name].label })),
  ];

  function submit(event: FormEvent): void {
    event.preventDefault();
    save.mutate({ platform: platform === AUTO ? null : platform, adminUrl: adminUrl.trim() ? adminUrl.trim() : null });
  }

  const field = save.isError ? errorField(save.error) : null;

  return (
    <Dialog
      open
      title="Платформа и админка"
      description={site.displayName}
      onClose={() => (save.isPending ? undefined : onClose())}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={save.isPending}>
            Отмена
          </Button>
          <Button variant="primary" icon={Save} busy={save.isPending} type="submit" form="site-platform">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="site-platform" className="stack" onSubmit={submit}>
        <Field label="Платформа" htmlFor="site-platform-name">
          <Select id="site-platform-name" label="Платформа" value={platform} onChange={setPlatform} options={options} />
        </Field>
        <Field
          label="Адрес админки"
          htmlFor="site-admin-url"
          hint="Пусто — определяется автоматически"
          {...(field === 'adminUrl' ? { error: 'Нужен адрес http:// или https://' } : {})}
        >
          <Input
            id="site-admin-url"
            value={adminUrl}
            onChange={(event) => setAdminUrl(event.target.value)}
            placeholder={site.adminUrlByOperator ? '' : (site.adminUrl ?? 'https://…')}
            invalid={field === 'adminUrl'}
            autoComplete="off"
            inputMode="url"
          />
        </Field>
        {save.isError && field !== 'adminUrl' ? (
          <p className="field-error" role="alert">
            {errorText(save.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
